import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { PRICING } from "../data/content";
import { fetchProduct } from "../lib/api";
import NotifyMeButton from "./NotifyMeButton";

/**
 * Sticky bottom Buy Now bar — mobile-first.
 * Reads live price/MRP from the backend so admin edits reflect instantly.
 * Hidden on checkout/order confirmation pages.
 */
const HIDDEN_ROUTES = ["/checkout", "/admin"];

const StickyBuyBar = ({ productId, productName }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { addItem, setOpen } = useCart();
  const { t } = useI18n();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchProduct()
      .then((p) => alive && setProduct(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [pathname]);

  if (HIDDEN_ROUTES.some((r) => pathname.startsWith(r))) return null;
  if (pathname.startsWith("/order/")) return null;

  const price = product?.price ?? PRICING.price;
  const mrp = product?.mrp ?? PRICING.mrp;
  const discountPercent = mrp
    ? Math.round(((mrp - price) / mrp) * 100)
    : PRICING.discountPercent;
  const outOfStock = product ? (product.stock ?? 0) <= 0 : false;

  const handleBuy = () => {
    addItem({
      id: product?.id || productId,
      name: product?.name || productName,
      price,
    });
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <div
      data-testid="sticky-buy-bar"
      className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[#F39C12]/30 shadow-[0_-8px_24px_-12px_rgba(44,62,80,0.15)]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2.5 flex items-center gap-3">
        <div className="flex-1 min-w-0 leading-tight">
          <div className="flex items-baseline gap-2">
            <span
              data-testid="sticky-buy-price"
              className="font-display text-xl sm:text-2xl font-bold text-[#D63031]"
            >
              ₹{price.toLocaleString("en-IN")}
            </span>
            <span className="text-xs sm:text-sm text-[#7F8C8D] line-through">
              ₹{mrp.toLocaleString("en-IN")}
            </span>
            <span className="px-1.5 py-0.5 bg-[#27AE60]/15 text-[#27AE60] text-[10px] font-bold rounded">
              {discountPercent}% {t("off")}
            </span>
          </div>
          <p className="text-[10px] sm:text-xs text-[#7F8C8D] truncate">
            {t("free_shipping")} · {t("cod_label")}
          </p>
        </div>
        <button
          data-testid="sticky-buy-btn"
          onClick={handleBuy}
          disabled={outOfStock}
          className={`cta-red rounded-full px-5 sm:px-7 h-12 inline-flex items-center gap-1.5 font-bold text-sm sm:text-base shrink-0 ${
            outOfStock ? "hidden" : ""
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          {t("sticky_buy_now")}
          <ChevronRight className="w-4 h-4 -mr-1 hidden sm:inline" />
        </button>
        {outOfStock && (
          <NotifyMeButton variant="compact" label="Notify me" />
        )}
      </div>
    </div>
  );
};

export default StickyBuyBar;
